import { CalendarDay } from '@shared/models/dateTypes';

import { HelpSection } from './models';

/**
 * The table of contents for Hjælp.
 *
 * The articles are markdown files under `src/assets/help/<section>/<slug>.md`, versioned with the
 * code so a feature and its help ship in the same PR. The manifest — not the files — decides what
 * exists and who sees it: an article whose section or self carries a `permission` is only listed,
 * searched and served for users that `UserService.canI` says yes to, the same keys that gate the
 * main menu; an `audience` keeps administrators' and associations' guides apart; a `feature` ties
 * a guide to a part of the app that only some tenants have, such as the association's KMD pages,
 * and shows it exactly where the menu has the item. Inside an article, a paragraph that only some
 * readers should see is wrapped in `::: permission <key>` … `:::` or `::: feature <key>` … `:::`
 * (see `stripPermissionBlocks`).
 *
 * The permissions decide what the page shows — nothing more. The files under `assets/help` are
 * served statically to anyone who can reach the app, signed in or not, so an article must never
 * hold anything that is confidential. Gate on permissions to keep the list relevant, not to
 * protect content.
 *
 * Order here is the order on the page.
 */
/** The slug of the release-notes section; the overview shows its newest article as "Seneste release". */
export const NEWS_SECTION_SLUG = 'nyheder';

const day = (value: string) => value as CalendarDay;

/** The day of the newest release note — what the menu's "Ny" badge compares the reader's last-seen date to. */
export const newestNewsDate = (): CalendarDay | null =>
  HELP_MANIFEST.find((s) => s.slug === NEWS_SECTION_SLUG)?.articles[0]?.date ?? null;

export const HELP_MANIFEST: readonly HelpSection[] = [
  {
    // One article per dev → main release that reached production, newest first. Written for the
    // customers' administrators, not from the commit log — the release skill adds the entry as part
    // of a release. Associations never see it: the log is for administrators only.
    slug: NEWS_SECTION_SLUG,
    title: 'Nyheder',
    icon: 'campaign',
    audience: 'admin',
    articles: [
      {
        slug: '2026-11-03',
        date: day('2026-11-03'),
        title: 'Gentagne bookinger virker igen, undtagelse fra MitID-kravet og aktivitetssider',
        summary:
          'Gentagne bookinger opretter tider igen, bookingdialogen passer til telefon og tablet, en enkelt bruger kan undtages fra rollens faste login-metode, rollen Administrator (read-only, alle anlæg) ser alle anlæg, Moduler er en oversigtsside, og borgersiderne har fået en side pr. aktivitet.',
      },
      {
        slug: '2026-10-29',
        date: day('2026-10-29'),
        title: 'Mails til kontaktpersonen og Stamdata for alle brugere',
        summary:
          'Bookingmails går til kontaktpersonen eller opretteren, når bookingens kunde ikke har en e-mail, Stamdata vises igen for brugere med fødselsdag 01-01-0001, "Vis i KMD" og bookinger til KMD bruger de rigtige KMD-numre, og fokusrammen på borgersidernes spørgsmål og svar er ikke længere skåret af.',
      },
      {
        slug: '2026-10-27',
        date: day('2026-10-27'),
        title: 'Prisperioder på flere anlæg og KMD-priser pr. kommune',
        summary:
          'Prisperioder vises under alle deres anlæg og har nyt udseende, KMD-felter er låst, KMD-prisernes interval og mindste bookingtid kan sættes pr. kommune, listevisningens statusfilter er ryddet op, foreningernes tider over flere dage viser slutdagen, teksten under e-mail-login kan tilpasses pr. kommune, og fokusrammerne på borgersiderne er tydeligere.',
      },
      {
        slug: '2026-10-26',
        date: day('2026-10-26'),
        title: 'Kundetyper låst til MitID, arrangementsforespørgsler og KMD-menuen',
        summary:
          'En kundetype kan kræve MitID ved login, arrangementsforespørgsler holdes ikke op mod bookingreglerne, KMD-siderne i menuen følger KMD for foreninger og virksomheder, hjælpen har én vejledning pr. KMD-menupunkt, og borgerappen har fået tydeligere fokus og kontrast.',
      },
      {
        slug: '2026-10-25',
        date: day('2026-10-25'),
        title: 'Kalender i Søg/Book, bookerens menu og lokaler efter KMD',
        summary:
          'Dag- og ugekalender i foreningernes Søg/Book, Gentag i kurven, rettigheder til foreningsbookerens menu, lokaler efter reglerne i KMD, lukning af organisationers fælles login og bekræftelse efter NemLog-in-logout.',
      },
      {
        slug: '2026-10-22',
        date: day('2026-10-22'),
        title: 'Afviste tider, søgning efter KMD og samlede bookingmails',
        summary:
          'Fanen Afvist i foreningens bookinger, søgning der kun viser bookbare faciliteter, gentagne tider samlet i bookingmails og virksomheder og interne kunder, der kan booke selv.',
      },
      {
        slug: '2026-10-21',
        date: day('2026-10-21'),
        title: 'Nye filtre, KMD-bookingnummer og dørkoder',
        summary:
          'Ens filtre på alle oversigter, anlægget huskes, KMD-bookingnummer, dørkoder efter lokalets marginer, hurtigere Mine bookinger for foreningerne og en række rettelser.',
      },
      {
        slug: '2026-10-07',
        date: day('2026-10-07'),
        title: 'Ny hjælp, åbningstider og foreningernes brugere',
        summary:
          'Indbygget hjælp, login i to trin, åbningstider på anlæggene, kopiering i bookingdialogen, dansk tid overalt, nye oversigter, foreningernes brugere og borgerportalen.',
      },
      {
        slug: '2026-09-17',
        date: day('2026-09-17'),
        title: 'Brugerstatus, bookingregler og visitering',
        summary:
          'Skift status på en bruger, alle bookingregler i én notits, alders- og bopælskrav, spærringer kan slettes.',
      },
      {
        slug: '2026-09-11',
        date: day('2026-09-11'),
        title: 'Tilvalg ved lokaleskift og MitID ved sletning',
        summary:
          'Tilvalg følger med til det nye lokale, slettede brugere kan oprettes igen med MitID, lokale på bookingkortet.',
      },
      {
        slug: '2026-09-09',
        date: day('2026-09-09'),
        title: 'Mine bookinger i portalen',
        summary:
          'Tre fejl rettet på borgerens bookingliste: menu, tomme dialoger og manglende bookinger.',
      },
      {
        slug: '2026-09-08',
        date: day('2026-09-08'),
        title: 'Betalingslink uden login',
        summary:
          'Betalingslink og kvittering virker uden login, og afsluttede bookinger er ude af leverandørmailen.',
      },
      {
        slug: '2026-09-07',
        date: day('2026-09-07'),
        title: 'Leverandørmail og infoskærmnavn',
        summary:
          'Antal på tilvalg i leverandørmailen, daglig/ugentlig mail pr. kunde, og infoskærmnavn ved booking i portalen.',
      },
      {
        slug: '2026-09-04',
        date: day('2026-09-04'),
        title: 'Dashboardet Mine opgaver udvides',
        summary:
          'Ny liste med dagens bookinger, anlægsvalg, kolonnevalg, flextid, Udestående-siden rettet, vedhæftninger og leverandørmail til Sygehusboliger.',
      },
    ],
  },
  {
    slug: 'kom-godt-i-gang',
    title: 'Kom godt i gang',
    icon: 'flag',
    articles: [
      {
        slug: 'saadan-bruger-du-hjaelpen',
        title: 'Sådan bruger du hjælpen',
        summary: 'Hvad du finder her, og hvordan du søger i vejledningerne.',
      },
      {
        slug: 'log-ind-og-profil',
        title: 'Log ind, vælg profil og se din egen profil',
        summary: 'Loginsiden, valget mellem flere profiler, Min profil og log ud.',
      },
      {
        slug: 'menuen-og-dashboardet',
        title: 'Menuen og dashboardet',
        summary: 'Sådan er menuen bygget op, og hvad "Mine opgaver" viser dig.',
        audience: 'admin',
      },
    ],
  },
  {
    slug: 'bookinger',
    title: 'Bookinger',
    icon: 'event',
    permission: 'manage_bookings',
    audience: 'admin',
    articles: [
      {
        slug: 'kalenderen',
        title: 'Bookingkalenderen',
        summary: 'Find rundt i kalenderen: filtre, visninger, tidsopløsning og listevisning.',
      },
      {
        slug: 'opret-booking',
        title: 'Opret en booking',
        summary: 'Træk en tid i kalenderen, vælg bestiller og udfyld bookingen.',
      },
      {
        slug: 'tilfoej-perioder',
        title: 'Tilføj flere tider til en booking',
        summary: 'Læg nye tider på en eksisterende booking, og kopiér oplysninger mellem tiderne.',
      },
      {
        slug: 'gentagne-bookinger',
        title: 'Gentagne bookinger',
        summary: 'Gentag en tid på faste ugedage i en periode, fx en sæson.',
      },
      {
        slug: 'afbook-tid',
        title: 'Afbook en tid',
        summary:
          'Afbook én tid, flere tider eller hele bookingen, og hvad der sker med betalingen.',
      },
      {
        slug: 'spaerringer',
        title: 'Spærringer',
        summary:
          'Spær et anlæg eller en facilitet i en periode, og håndter de bookinger, der ligger i vejen.',
      },
      {
        slug: 'listevisning',
        title: 'Listevisning, eksport og notifikationer',
        summary:
          'Find bookinger i en tabel, eksportér til Excel og send besked til flere bestillere.',
      },
      {
        slug: 'forespoergsler',
        title: 'Godkend eller afvis forespørgsler',
        summary:
          'Listen over forespørgsler, og hvordan du godkender eller afviser tiderne i bookingen.',
      },
      {
        slug: 'udestaaende-og-balance',
        title: 'Udestående betalinger og Balance',
        summary: 'Se hvem der skylder, tilbagefør betalinger, og godkend posteringer i Balance.',
      },
      {
        slug: 'omklaedningsrum',
        title: 'Omklædningsrum i bookinger',
        summary: 'Omklædningskalenderen og fanen Omklædning i bookingen.',
      },
      {
        slug: 'doerkoder',
        title: 'Dørkoder',
        summary: 'Dørene med dagens koder, loggen over indtastede koder, og hvor koderne oprettes.',
        permission: 'manage_codes',
      },
    ],
  },
  {
    slug: 'brugere',
    title: 'Brugere',
    icon: 'group',
    permission: 'manage_users',
    audience: 'admin',
    articles: [
      {
        slug: 'find-og-opret-brugere',
        title: 'Find og opret brugere',
        summary: 'Søg efter brugere, læs listen, og opret en ny bruger.',
      },
      {
        slug: 'rediger-bruger',
        title: 'Rediger en bruger og skift status',
        summary: 'Stamdata, godkendelse og afvisning, roller, anlæg og sletning.',
      },
      {
        slug: 'brugerens-faner',
        title: 'Brugerens bookinger, rettigheder, maillog, koder og login',
        summary: 'De øvrige faner på brugeren: hvad de viser, og hvad du kan gøre der.',
      },
    ],
  },
  {
    slug: 'anlaeg',
    title: 'Anlæg',
    icon: 'home',
    permission: 'manage_facilities',
    audience: 'admin',
    articles: [
      {
        slug: 'faciliteter',
        title: 'Anlæg, områder og faciliteter',
        summary:
          'Sådan hænger anlæg, områder og faciliteter sammen, og hvordan du opretter og retter dem.',
      },
      {
        slug: 'beskrivelse-billeder-og-tilvalg',
        title: 'Beskrivelse, billeder og tilvalg på en facilitet',
        summary: 'Tekster og billeder til borgersiden, og de tilvalg der kan købes med.',
      },
      {
        slug: 'prisperioder',
        title: 'Prisperioder',
        summary:
          'Priser pr. ugedag og aktivitet, hvem tiderne udbydes til, udbudstider, faste banetider og udgivelse.',
        permission: 'manage_schedules',
      },
      {
        slug: 'bookingregler',
        title: 'Bookingregler: maksimumtid, minimum, buffer og visitering',
        summary: 'Reglerne på en prisperiode, og hvordan de virker for borgere og administratorer.',
        permission: 'manage_schedules',
      },
      {
        slug: 'aabningstider',
        title: 'Åbningstider',
        summary:
          'Opsætninger, hvem og hvilke lokaler de gælder for, perioder pr. ugedag, helligdage, KMD, kopiering og preview.',
      },
      {
        slug: 'omklaedningsrum-opsaetning',
        title: 'Opsætning af omklædningsrum',
        summary: 'Opret omklædningsrum, knyt dem til faciliteter, og sæt tillægstid og tags.',
      },
    ],
  },
  {
    slug: 'foreninger',
    title: 'Foreninger',
    icon: 'holiday_village',
    permission: 'manage_others_associations',
    audience: 'admin',
    articles: [
      {
        slug: 'administration-af-foreninger',
        title: 'Administration af foreninger',
        summary:
          'Website tekst, bookinger og brugere set fra administrationen, og hvor forespørgsler og nye brugere godkendes.',
      },
    ],
  },
  {
    slug: 'abonnementer',
    title: 'Abonnementer',
    icon: 'credit_card',
    permission: 'manage_subscriptions',
    audience: 'admin',
    articles: [
      {
        slug: 'opsaetning',
        title: 'Opret og rediger abonnementer',
        summary: 'Listen over abonnementer og de tre trin, når du opretter eller retter et.',
      },
      {
        slug: 'medlemsskaber',
        title: 'Medlemsskaber',
        summary: 'Se hvem der har tegnet et abonnement, afmeld, og send besked til medlemmerne.',
        permission: 'manage_usersubscriptions',
      },
    ],
  },
  {
    slug: 'indstillinger',
    title: 'Indstillinger',
    icon: 'settings',
    permission: 'manage_settings',
    audience: 'admin',
    articles: [
      {
        slug: 'cms-og-skabeloner',
        title: 'CMS og mailskabeloner',
        summary: 'Ret teksterne på borgersiden og de mails, systemet sender.',
      },
      {
        slug: 'tags',
        title: 'Tags',
        summary: 'Opret og ret tags, og hvad de bruges til.',
      },
      {
        slug: 'mail',
        title: 'Mailserver',
        summary: 'SMTP-indstillinger og afsender på systemets mails.',
      },
      {
        slug: 'rettigheder',
        title: 'Rettigheder pr. rolle',
        summary: 'Hvad hver rolle må, og hvordan du ændrer det.',
        permission: 'manage_permissions',
      },
      {
        slug: 'import',
        title: 'Import af bookinger',
        summary: 'Importér bookinger eller prisperioder fra en fil.',
        permission: 'manage_import',
      },
    ],
  },
  {
    slug: 'min-forening',
    title: 'Søg/Book og Min forening',
    icon: 'holiday_village',
    audience: 'association',
    articles: [
      {
        slug: 'vaelg-forening',
        title: 'Vælg forening',
        summary:
          'Hvad der sker, når du logger ind, hvordan du skifter forening, og hvad menuen indeholder.',
      },
      {
        slug: 'kmd-grundoplysninger',
        title: 'Grundoplysninger',
        summary: 'Ret foreningens oplysninger i KMD, og kom i gang første gang.',
        feature: 'kmd_menu_links',
      },
      {
        slug: 'kmd-kontaktpersoner',
        title: 'Kontaktpersoner',
        summary: 'Opret foreningens kontaktpersoner i KMD.',
        feature: 'kmd_menu_links',
      },
      {
        slug: 'kmd-brugere',
        title: 'Brugere (KMD)',
        summary: 'Se og opret foreningens brugere og roller i KMD.',
        feature: 'kmd_menu_links',
      },
      {
        slug: 'kmd-dokumenter',
        title: 'Dokumenter (KMD)',
        summary: 'Aflever de dokumenter, kommunen beder om, i KMD.',
        feature: 'kmd_menu_links',
      },
      {
        slug: 'kmd-oevrige-punkter',
        title: 'Startside, Tilskud og Min konto (KMD)',
        summary: 'De øvrige punkter i menuen, der åbner KMD.',
        feature: 'kmd_links',
      },
      {
        slug: 'soeg-og-book',
        title: 'Søg ledige tider',
        summary: 'Booking og Kamp: søg på aktivitet, sted og dato, og forstå tidernes farver.',
      },
      {
        slug: 'kurv-og-bestilling',
        title: 'Kurven og bestillingen',
        summary: 'Læg tider i kurven, gentag dem, og gennemfør bestillingen trin for trin.',
      },
      {
        slug: 'arrangement',
        title: 'Anmod om et arrangement',
        summary: 'Forespørgsler på større arrangementer over flere dage eller hele anlæg.',
      },
      {
        slug: 'mine-bookinger',
        title: 'Mine bookinger',
        summary: 'Overblik over bookinger og tider, filtre, afbooking og redigering af en tid.',
      },
      {
        slug: 'foreningens-brugere',
        title: 'Foreningens brugere',
        summary: 'Se foreningens brugere, tilknyt nye, og skift mellem ansvarlig og booker.',
      },
      {
        slug: 'stamdata-og-billeder',
        title: 'Foreningens website tekst og billede',
        summary: 'Teksten om foreningen og billedet på borgersiden.',
        permission: 'view_association_website_text',
      },
    ],
  },
  {
    slug: 'spoergsmaal',
    title: 'Ofte stillede spørgsmål',
    icon: 'quiz',
    articles: [
      {
        slug: 'ofte-stillede-spoergsmaal',
        title: 'Ofte stillede spørgsmål',
        summary: 'Korte svar på de spørgsmål, support oftest får.',
        audience: 'admin',
      },
      {
        slug: 'foreningens-spoergsmaal',
        title: 'Spørgsmål fra foreninger',
        summary:
          'Login, manglende forening, ingen ledige tider, kurven, omklædningsrum og bekræftelser.',
        audience: 'association',
      },
    ],
  },
];
